<template>
  <VerificationForm :skip-button="loaded && !email_sended" :loader="!loaded" @skip-click="onSkipButtonClick">
    <template #title>{{ title }}</template>
    <template #description>
      <template v-if="!email_sended"> {{ $t('phoneSignedTitle') }}<br />{{ $t('phoneSIgnedDescription') }} </template>
      <template v-else>
        {{ $t('confirmEmailDescription') }}
        <span class="email-verification-description__phone-number">{{ email }}</span>
      </template>
    </template>
    <template #input v-if="!email_sended">
      <ValidationField :errors="r$?.$errors?.email">
        <SInput
          v-model="email"
          label="E-mail"
          :class="{
            'invalid--element-border': r$.$errors?.email?.length > 0,
          }"
        ></SInput>
      </ValidationField>
    </template>
    <template #actions>
      <SButton v-if="!email_sended" :disabled="!email_valid" wide @click="onSendMailClick">{{
        $t('addEmailButtonLabel')
      }}</SButton>
      <SButton v-if="email_sended" wide @click="onSkipButtonClick">{{ $t('toLKButtonLabel') }}</SButton>
      <div v-if="email_sended" class="email-verification-actions__timer">
        {{ $t('successTimerBeforePart') }} {{ timer_string }} {{ $t('successTimerAfterPart') }}
      </div>
    </template>
  </VerificationForm>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRefs } from 'vue'
import { SInput, SButton } from '@synergy/ui-kit-vue3'
import { withMessage, email as emailValidation } from '@regle/rules'
import { useTranslate } from '@tolgee/vue'
import { useRegle } from '@regle/core'

//Components
import ValidationField from '@/components/ValidationField/ValidationField.vue'
import VerificationForm from '../VerrificationForm/VerificationForm.vue'

// Helpers
import { setAuthForm, sendMail } from '@/api/api'

const emit = defineEmits(['skip-click'])

const props = defineProps({
  token: {
    type: String,
    default: '',
  },
})

const { token } = toRefs(props)

const { t } = useTranslate()

const loaded = ref(true)
const email = ref('')
const email_sended = ref(false)
const timer_string = ref('00')
const send_code_timer = ref<number | null>(null)

const email_valid = computed(() => {
  return !!email.value
})

const rules = computed(() => {
  return {
    email: {
      email: withMessage(emailValidation, t.value('incorrectEmailError')),
    },
  }
})

const { r$ } = useRegle({ email }, rules, {
  autoDirty: false,
})

const title = computed(() => {
  if (!email_sended.value) {
    return t.value('addEmailTitle')
  } else {
    return t.value('confirmEmailTitle')
  }
})

function startCodeTimer(expires: number) {
  if (send_code_timer.value) {
    clearInterval(send_code_timer.value)
  }

  send_code_timer.value = setInterval(function () {
    if (expires > 0) {
      expires = expires - 1
      const minutes = Math.floor(expires / 60)
      const seconds = expires - minutes * 60
      timer_string.value = formatTimeString(seconds, '0', 2)
    } else {
      send_code_timer.value && clearInterval(send_code_timer.value)
      timer_string.value = '00'
      onSkipButtonClick()
    }
  }, 1000)
}

function formatTimeString(timeString: number, pad: string, length: number) {
  return (new Array(length + 1).join(pad) + timeString).slice(-length)
}

function onSendMailClick() {
  r$.$validate().then((data) => {
    if (data?.valid) {
      loaded.value = false
      sendMail(token.value, email.value, 'https://lk.synergy.ru/')
        .then((resp: { type: string }) => {
          if (resp?.type === 'application_error') {
            loaded.value = true
          } else {
            setAuthForm(token.value).then(() => {
              loaded.value = true
              email_sended.value = true
              startCodeTimer(10)
            })
          }
        })
        .catch(() => {
          loaded.value = true
        })
    }
  })
}

function onSkipButtonClick() {
  emit('skip-click')
}

onMounted(() => {
  // fetch(`${ENV.VERIFICATION__API_URL}/v1/show_auth_form`, {
  //   method: 'PUT',
  //   body: JSON.stringify({ show_auth_form: true }),
  //   headers: {
  //     Authorization: token.value,
  //     'Content-Type': 'application/json',
  //   },
  // })
})
</script>

<style scoped lang="scss">
.email-verification {
  display: flex;
  flex-direction: column;
  gap: 36px;
  max-width: 540px;
  width: 100%;
  border-radius: 4px;
  padding: 40px;
  overflow: hidden;
  box-sizing: border-box;

  box-shadow: 0px 6px 18px 0px #00000017;
}

.email-verification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__logo {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    height: 48px;
  }
}

.email-verification-body {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__label {
    font-weight: 500;
    font-size: 28px;
    line-height: 36px;
    letter-spacing: 0%;
    text-align: center;
  }
}

.email-verification-description {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0%;
  text-align: center;

  &__phone-number {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0%;
    text-align: center;
  }
}

.email-verification-actions {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;

  &__timer {
    white-space: pre-line;
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    letter-spacing: 0%;
    text-align: center;
    vertical-align: middle;
    color: var(--color-text-secondary);
  }
}

.verification-preloader {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 246px;
}

.skip-button {
  display: flex;
  background-color: transparent;
  padding: 0;
  margin: 0;
  border: none;
  color: #ed131c;
  cursor: pointer;

  &__label {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-weight: 500;
    font-size: 18px;
    line-height: 18px;
    letter-spacing: 0%;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }
}

.invalid {
  // border-color: var(--color-text-accent) !important;
  &--element-border {
    border-radius: 4px;
    border: 1px var(--color-text-accent) solid !important;
  }

  &--text {
    color: var(--color-icon-accent-dangerous) !important;
  }

  &--card-border {
    border-color: var(--color-icon-accent-dangerous) !important;
  }
}
</style>
